// utils/calculateTotalFees.js
import { getPlatformFeeMinor, getStripeFeeConfig, grossUpForStripeFeeMinor } from './fees';

const MIN_CADENCE_ADJUSTMENT_PCT = -1000;
const MAX_CADENCE_DISCOUNT_PCT = 100;

export function normalizeCadenceDiscountPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(MIN_CADENCE_ADJUSTMENT_PCT, Math.min(MAX_CADENCE_DISCOUNT_PCT, n));
}

/**
 * Calculate the member-facing total based on:
 * - cheapest applicable base (already chosen upstream) passed as `baseAmount` (in pence)
 * - cadence (unit/count) with optional signed discount/premium %
 * - platform fee handling ('add'|'deduct') — when 'add', pro-rate platform fee
 * - stripe fee handling ('add'|'deduct') — when 'add', buffer % + fixed
 *
 * Supports both the **new** API and a **legacy** fallback:
 * New:
 *   calculateTotalFees({ baseAmount, cadence:{ unit:'month'|'year', count:1, discountPct:0 }, platformFeeHandling, stripeFeeHandling })
 * Legacy (mapped internally):
 *   calculateTotalFees({ baseAmount, renewalEvery, renewalUnit, platformFeeHandling, stripeFeeHandling })
 */
export function calculateTotalFees({
  baseAmount = 0,                 // pence, already scaled to the chosen cycle (month or year)
  platformFeeHandling = 'deduct', // 'add' | 'deduct'
  stripeFeeHandling = 'deduct',   // 'add' | 'deduct'
  currency = 'GBP',
  platformFeeMinor,
  stripeFeeCfg,
  cadence = { unit: 'year', count: 1, discountPct: 0 }, // unit in {'month','year'}
}) {
  const safeBase = Math.max(0, parseInt(baseAmount, 10));

  // Apply signed cadence adjustment to the base: positive discounts, negative premiums.
  const discountPct = normalizeCadenceDiscountPct(cadence?.discountPct);
  const discountedBase = Math.round(safeBase * (1 - discountPct / 100));

  // Platform fee (pro-rated by selected cadence; only month/year supported)
  const unit = cadence?.unit === 'month' ? 'month' : 'year';
  const count = Number(cadence?.count || 1);
  const cyclesPerYear = unit === 'month' ? 12 : 1;
  const perYearPlatformFee = Number.isFinite(platformFeeMinor)
    ? Number(platformFeeMinor || 0)
    : getPlatformFeeMinor({ currency });
  const platformFee =
    platformFeeHandling === 'add'
      ? Math.round(((perYearPlatformFee ?? 0) * count) / cyclesPerYear)
      : 0;

  // Stripe buffer if added to member total
  const subtotal = discountedBase + platformFee;
  const resolvedStripeCfg = stripeFeeCfg || getStripeFeeConfig({ currency });
  const finalAmount =
    stripeFeeHandling === 'add'
      ? grossUpForStripeFeeMinor(subtotal, resolvedStripeCfg)
      : subtotal;
  const stripeFee = Math.max(0, finalAmount - subtotal);

  return {
    baseAmount: discountedBase,
    platformFee,
    stripeFee,
    finalAmount,
  };
}
